/**
 * Checkout Success Page
 * Shown after successful Stripe checkout
 * Supports: i18n (en/ar), RTL, light/dark mode via CSS vars.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getSubscription } from '@/shared/services/billingApi';
import { useAppDispatch } from '@/shared/app/hooks';
import { fetchSubscription } from '@/shared/features/billing/billingSlice';
import { latinDigitsLocale } from '@/shared/lib/numberFormat';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const dispatch = useAppDispatch();
  const [subscription, setSubscription] = useState<any>(null);
  const [planName, setPlanName] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  // Clear any stored coupon after successful checkout
  useEffect(() => {
    try { localStorage.removeItem("midash_coupon"); } catch {}
  }, []);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        let resolvedPlan = 'free';
        let data: any = null;
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          data = await getSubscription();
          resolvedPlan = data.plan || 'free';
          if (resolvedPlan !== 'free' || data.is_trial || data.subscription) break;
        }
        setSubscription(data?.subscription || null);
        setPlanName(resolvedPlan);
        setIsTrial(data?.is_trial || data?.subscription?.status === 'trialing');
        // Push the freshly resolved plan into Redux so the rest of the app
        // (header badge, chat gates, AI Connections tab) sees the new tier
        // immediately instead of the stale 'free' from the pre-checkout
        // mount of AuthenticatedLayout.
        dispatch(fetchSubscription());
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSubscription();
  }, [sessionId]);

  const displayPlan =
    planName === 'pro_plus' ? 'Pro+'
    : planName === 'lite' ? 'Lite'
    : planName === 'byoai' ? 'BYOAI'
    : planName === 'pro' ? 'Pro'
    : 'Pro';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-bg)]" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[var(--accent-primary)] mx-auto mb-4" />
          <p className="text-[var(--color-text)]">{t('pricing.checkout.settingUp', 'Setting up your subscription...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-bg)] p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-md w-full bg-[var(--surface-panel)] rounded-xl border border-[var(--border-soft)] p-8 text-center">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-green-500" />
        </div>

        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">
          {planName === 'pro_plus'
            ? t('pricing.checkout.welcomeProPlus', 'Welcome to MiDash Pro+!')
            : planName === 'lite'
              ? t('pricing.checkout.welcomeLite', 'Welcome to MiDash Lite!')
              : planName === 'byoai'
                ? t('pricing.checkout.welcomeByoai', 'Welcome to MiDash BYOAI!')
                : t('pricing.checkout.welcomePro', 'Welcome to MiDash Pro!')}
        </h1>

        <p className="text-[var(--color-text-soft)] mb-6">
          {isTrial
            ? t('pricing.checkout.trialActive', 'Your free trial is now active. Enjoy full access to all premium features!')
            : t('pricing.checkout.subscriptionActive', 'Your subscription is now active. You have full access to all premium features.')}
        </p>

        {subscription && (
          <div className="bg-[var(--surface-bg)] rounded-lg p-4 mb-6" style={{ textAlign: isRTL ? 'right' : 'left' }}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[var(--color-text-soft)]">{t('pricing.plans.free.name', 'Plan')}</span>
              <span className="font-semibold text-[var(--color-text)]">{subscription.plan_name}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[var(--color-text-soft)]">{t('common.status', 'Status')}</span>
              <span className="text-green-500 font-medium capitalize">{subscription.status}</span>
            </div>
            {subscription.current_period_end && (
              <div className="flex justify-between items-center">
                <span className="text-[var(--color-text-soft)]">{t('pricing.billing.nextBilling', 'Next billing')}</span>
                <span className="text-[var(--color-text)]">
                  {new Date(subscription.current_period_end).toLocaleDateString(latinDigitsLocale(isRTL ? 'ar-SA' : 'en-US'))}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/platform')}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[var(--accent-primary)] text-[var(--text-on-accent)] rounded-lg hover:brightness-110 transition-all font-medium"
          >
            {t('pricing.checkout.goToPlatform', 'Go to Platform')}
            <ArrowRight size={18} />
          </button>

          <button
            onClick={() => navigate('/billing')}
            className="w-full px-6 py-3 border border-[var(--border-soft)] text-[var(--color-text)] rounded-lg hover:bg-[var(--surface-bg)] transition-all"
          >
            {t('pricing.checkout.manageSubscription', 'Manage Subscription')}
          </button>
        </div>

        <p className="mt-6 text-xs text-[var(--color-text-soft)]">
          {t('pricing.checkout.receiptNote', "You'll receive a confirmation email shortly with your receipt.")}
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
